<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSidebarPanel from './AppSidebarPanel.vue'
import AppSidebarSheet from './AppSidebarSheet.vue'
import type { NavBadge, NavBrand, NavUser } from './nav-types'
import { SIDEBAR_KEYBOARD_SHORTCUT, useSidebar } from './useSidebar'
import AppResizeHandle from '../../primitives/AppResizeHandle/AppResizeHandle.vue'
import AppShortcut from '../../primitives/AppShortcut/AppShortcut.vue'
import AppTooltip from '../../primitives/AppTooltip/AppTooltip.vue'
import { clampWidth, measureCssLengths, useEdgeResize } from '@/composables/useEdgeResize'
import type { OnboardingChainStepView } from '@/types/onboarding-chain'

withDefaults(
  defineProps<{
    user?: NavUser | null
    brand?: NavBrand | null
    navBadges?: Record<string, NavBadge>
    /** Setup mode: the chain's steps take the place of the create button, the nav and the chats. */
    setup?: boolean
    /** The steps to list in setup mode, in chain order. */
    setupSteps?: readonly OnboardingChainStepView[]
    /** Turns on while setup hands over to Home, which lifts the setup steps away instead of dropping them. */
    handover?: boolean
    /** The theme on screen now; the switch at the foot of the nav shows it. */
    theme?: 'light' | 'dark'
  }>(),
  { user: null, brand: null, setup: false, setupSteps: () => [], handover: false, theme: 'light' },
)

const emit = defineEmits<{
  'sign-out': []
  'update:theme': [value: 'light' | 'dark']
}>()

const sidebar = useSidebar()
const { isMobile, state } = sidebar
const { t } = useI18n()

const rail = computed(() => state.value === 'rail')

const bounds = ref({ min: 0, max: 0 })
const railWidth = ref(0)
function refreshBounds() {
  const [min = 0, max = 0, icon = 0] = measureCssLengths([
    '--sidebar-width-min',
    '--sidebar-width-max',
    '--sidebar-width-icon',
  ])
  bounds.value = { min, max }
  railWidth.value = icon
}

const snapping = ref(false)

function applyWidth(px: number, persist: boolean) {
  if (px < bounds.value.min / 2) {
    if (!rail.value) sidebar.setState('rail')
    return
  }
  if (rail.value) sidebar.setState('expanded')
  sidebar.setWidth(clampWidth(px, bounds.value), persist)
}

function currentWidth(): number {
  return rail.value ? railWidth.value : clampWidth(sidebar.width.value, bounds.value)
}

const resize = useEdgeResize({
  edge: 'right',
  getWidth: currentWidth,
  getBounds: () => bounds.value,
  setWidth: applyWidth,
  onClick: sidebar.toggle,
  onActivate: sidebar.toggle,
})
const { dragging } = resize
const handlers = {
  onPointerdown: resize.onPointerdown,
  onPointermove: resize.onPointermove,
  onPointerup: resize.onPointerup,
  onPointercancel: resize.onPointercancel,
  onKeydown: resize.onKeydown,
}

watch(state, () => {
  if (dragging.value) snapping.value = true
})
watch(dragging, (value) => {
  if (!value) snapping.value = false
})
function onSpacerTransitionEnd(event: TransitionEvent) {
  if (event.propertyName === 'width') snapping.value = false
}

const transitionClass = computed(() =>
  dragging.value && !snapping.value ? '' : 'transition-[width] duration-slow ease-soft',
)

const widthClass = computed(() =>
  rail.value ? 'w-(--sidebar-width-icon)' : 'w-(--sidebar-width-effective)',
)

const valueNow = computed(() => Math.round(currentWidth()))
const valueMin = computed(() => Math.round(bounds.value.min))
const valueMax = computed(() => Math.round(bounds.value.max))

const hint = computed(() =>
  rail.value ? t('shell.expandSidebarHint') : t('shell.resizeSidebarHint'),
)

const handleClass = computed(() => [
  '-translate-x-1/2',
  rail.value ? 'left-(--sidebar-width-icon)' : 'left-(--sidebar-width-effective)',
  dragging.value && !snapping.value ? '' : 'transition-[left] duration-slow ease-soft',
])

onMounted(() => {
  refreshBounds()
  window.addEventListener('resize', refreshBounds)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', refreshBounds)
})
</script>

<template>
  <AppSidebarSheet
    v-if="isMobile"
    :user="user"
    :brand="brand"
    :nav-badges="navBadges"
    :setup="setup"
    :setup-steps="setupSteps"
    :handover="handover"
    :theme="theme"
    @sign-out="emit('sign-out')"
    @update:theme="emit('update:theme', $event)"
  >
    <template #nav-footer><slot name="nav-footer" /></template>
  </AppSidebarSheet>
  <template v-else>
    <div
      class="h-svh shrink-0"
      :class="[widthClass, transitionClass]"
      @transitionend="onSpacerTransitionEnd"
    />
    <div class="fixed inset-y-0 left-0 z-10 h-svh" :class="[widthClass, transitionClass]">
      <AppSidebarPanel
        :user="user"
        :brand="brand"
        :nav-badges="navBadges"
        :setup="setup"
        :setup-steps="setupSteps"
        :handover="handover"
        :theme="theme"
        @sign-out="emit('sign-out')"
        @update:theme="emit('update:theme', $event)"
      >
        <template #nav-footer><slot name="nav-footer" /></template>
      </AppSidebarPanel>
    </div>
    <div v-if="!setup" class="fixed inset-y-0 z-20 w-4" :class="handleClass">
      <AppTooltip placement="right" :open="dragging ? false : undefined">
        <template #trigger="{ aria }">
          <AppResizeHandle
            :label="t('shell.resizeSidebar')"
            :value-now="valueNow"
            :value-min="valueMin"
            :value-max="valueMax"
            :dragging="dragging"
            v-bind="{ ...aria, ...handlers }"
          />
        </template>
        <template #content>
          <span class="flex items-center gap-4">
            {{ hint }}
            <AppShortcut :shortcut="SIDEBAR_KEYBOARD_SHORTCUT" />
          </span>
        </template>
      </AppTooltip>
    </div>
  </template>
</template>
